// src/primitives/focus.ts
var CYAN_DEEP = "#008C99";
var LINE = `0 0 0 1px #fff, 0 0 0 2.5px ${CYAN_DEEP}`;
var BLOOM = "0 0 0 4.5px rgba(228,255,254,.75), 0 0 10px 2px rgba(157,237,227,.4)";
var focus = {
  /** `--tl-focus-line` resolved — the die-cut + compliant line pair. */
  line: LINE,
  /** `--tl-focus-bloom` resolved — the signature frost bloom. */
  bloom: BLOOM,
  /** `--tl-focus` resolved — the full three-layer ring. Standalone controls want this. */
  ring: `${LINE}, ${BLOOM}`,
  /**
   * `--tl-focus-tight` resolved — the line layers only. For DENSE ROWS (table cells, chart
   * controls, inputs) where the bloom would clip or crowd its neighbours. Assign it per
   * consumer; never blanket-alias `ring` to it.
   */
  tight: LINE,
  /** `--tl-focus-on-dark` resolved — the ring inverted for dark surfaces. */
  onDark: "0 0 0 1px rgba(11,42,48,.9), 0 0 0 2.5px #BFF6F1, 0 0 12px 3px rgba(157,237,227,.38)"
};
var focusCss = {
  /** `--tl-focus-line` */
  line: "0 0 0 1px #fff, 0 0 0 2.5px var(--tl-cyan-deep)",
  /** `--tl-focus-bloom` — no `var()` in canonical, so it is the resolved value. */
  bloom: BLOOM,
  /** `--tl-focus` */
  ring: "var(--tl-focus-line), var(--tl-focus-bloom)",
  /** `--tl-focus-tight` */
  tight: "var(--tl-focus-line)",
  /** `--tl-focus-on-dark` — no `var()` in canonical, so it is the resolved value. */
  onDark: focus.onDark,
  /**
   * `--tl-brand-focus-ring` — an ALIAS since 2026-07-27. Every surface already routes through
   * this name, so the new ring lands everywhere at once. Prefer `--tl-focus` in new CSS and
   * `focus.ring` / `focus.tight` in new TS.
   */
  brandFocusRing: "var(--tl-focus)"
};

// src/primitives/color.ts
var brand = {
  /** Primary brand — logo, label accents, flat brand fallback. --tl-cyan */
  cyan: "#00BBCC",
  cyanHover: "#00A5B3",
  cyanPress: "#008C99",
  /**
   * Deep aqua — the ink of a SELECTED / ACTIVE item on a light surface. One step past
   * `cyanPress`; reads as committed state rather than a pressed button. --tl-cyan-selected
   */
  cyanSelected: "#007A86",
  /** Primary-button "candy gel" three-stop gradients (as authored). */
  gelIdle: "linear-gradient(175deg,#6FDBD2 0%,#34CED7 45%,#29C3D8 100%)",
  gelHover: "linear-gradient(175deg,#A1E7E0 0%,#42DBD0 45%,#29C3D8 100%)",
  gelPress: "linear-gradient(175deg,#6FDBD2 0%,#5ED3DC 45%,#1BBDE3 100%)",
  glow: "0 6px 20px rgba(27,182,217,.35)",
  glowHover: "0 6px 24px rgba(27,182,217,.50)",
  pressInset: "inset 0 2px 6px rgba(0,0,0,.12)",
  /**
   * The focus ring's WCAG-carrying line colour. --tl-cyan-deep
   *
   * Same value as `cyanPress`; canonical re-declares it under its own name because the focus
   * system is what makes it load-bearing (4.03:1 on white). See primitives/focus.ts.
   */
  cyanDeep: CYAN_DEEP,
  /**
   * The shared focus ring. --tl-brand-focus-ring
   *
   * Since the 2026-07-27 pull this is an ALIAS of `--tl-focus` — the three-layer "sticker
   * outline" — replacing the old `0 0 0 5px rgba(157,237,227,.45)` cyan-mint halo, which was
   * ~1.15:1 and effectively invisible. Every focus surface in the repo already routes through
   * this name, so the fix lands everywhere at once.
   *
   * This is the RESOLVED literal (no `var()`), because it is interpolated into
   * styled-components. Prefer `focus.ring` in new code, and `focus.tight` in dense rows.
   */
  focusRing: focus.ring,
  /**
   * TL Aqua, the brand gradient's end (the design project's README: "TL Cyan to TL Aqua"). --tl-aqua
   *
   * AHEAD of the design project (`design-source/PROVENANCE.md`, divergence 11): added in the repo on
   * 2026-11-01 by Rob's ruling (`spec-plan-machine-rotate-ring`, R11), for the 3D plan's machine turn
   * ring. Not `--tl-support-aqua` (#A0E6EB, a ribbon wash) nor `--tl-ink-on-aqua` (text).
   */
  aqua: "#44DDCC",
  /** The brand gradient, the selection cyan to TL Aqua. --tl-brand-gradient (divergence 11) */
  gradient: "linear-gradient(90deg, #1BB6D9 0%, #44DDCC 100%)",
  /** Glow / selection halo. --tl-frost */
  frost: "#E4FFFE",
  frostGlow: "rgba(228, 255, 254, 0.55)"
};
var ink = {
  /** fg-1, primary text. --tl-ink */
  1: "#3F454E",
  /** fg-2, secondary text. --tl-ink-2 */
  2: "#565C66",
  /** fg-3, body. --tl-ink-3 */
  3: "#6D737E",
  /** fg-4, muted, icons. --tl-ink-4 */
  4: "#7F8691",
  /** fg-5, placeholder. --tl-ink-5 */
  5: "#A0A6B1",
  /** description text (52% alpha). --tl-ink-muted */
  muted: "#3F454E85",
  /**
   * The ink that goes ON a solid semantic fill (`semanticColor.error` / `errorStrong` /
   * `successStrong` / `warnStrong`, and the brand gel) — never on a pale `-surface` tint,
   * which takes `inkOn.*`. Not the same thing as `surface[0]`: that is a surface, this is
   * an ink. --tl-ink-inverse
   *
   * CONTRAST CAVEAT (from the design source): at badge size these fills land ~3.2:1 with
   * this ink, which clears WCAG 1.4.11 for the fill as a non-text element but NOT 4.5:1 for
   * the label. Do not pair such a badge with information available nowhere else.
   */
  inverse: "#FFFFFF"
};
var surface = {
  /** pure white. --tl-surface-0 */
  0: "#FFFFFF",
  /** app background. --tl-surface-1 */
  1: "#F6F8FC",
  /** panel tint. --tl-surface-2 */
  2: "#EDF3FA",
  /** accent tint (warm). --tl-surface-3 */
  3: "#F5F0FA",
  /** accent tint (cool). --tl-surface-4 */
  4: "#EDE7F7",
  /** hover wash. --tl-surface-hover */
  hover: "#EDF3FA"
};
var line = {
  /** line on white. --tl-line-1 */
  1: "#D9DEE8",
  /** default border. --tl-line-2 */
  2: "#E1E4EE",
  /** warm divider. --tl-line-3 */
  3: "#E6E1EC",
  /** faint row divider. --tl-line-4 */
  4: "#EEF1F6",
  /** strong selected. --tl-line-selected */
  selected: "#3F454E",
  /**
   * The rest-state edge of an interactive control on white — checkbox, radio, input, stepper.
   * --tl-line-control
   *
   * Deliberately one step darker than `line[1]` so a control reads as operable rather than as
   * a divider: it is NOT a near-miss of `--tl-line-1`. **Do NOT use for dividers.** Canonical
   * also blesses it as the neutral "no signal" categorical rail, paired with `inkOn.idle`.
   */
  control: "#C7D0DE"
};
var scroll = {
  /** Hover/drag track on glass. --tl-scroll-track-glass */
  trackGlass: "rgba(96, 100, 158, 0.22)",
  /** The inset-recess ink that partners `trackGlass` — tinted, never pure black. --tl-scroll-track-glass-recess */
  trackGlassRecess: "rgba(50, 55, 110, 0.20)",
  /** On-solid track + thumb base. --tl-scroll-solid-base */
  solidBase: "#4283A7",
  /** `solidBase` as rgb channels, for `rgba(...)` composition at .05–.82. --tl-scroll-solid-base-rgb */
  solidBaseRgb: "66, 131, 167",
  /** The inset-recess ink that partners the on-solid track. --tl-scroll-solid-recess */
  solidRecess: "rgba(30, 66, 86, 0.14)"
};
var disabled = "#D9DEE8";
var accent = {
  pink: "#FF6FBF",
  violet: "#B15FFF",
  peach: "#FF8FAE",
  butter: "#FFC86B",
  /**
   * The being-edited role's model colour, and nothing else (Rob's ruling of 2026-10-30): a 3D plan
   * machine being moved or identified is drawn in it at full strength, because `butter` reads as a
   * pale orange beside the problem pink on a model. `butter` stays the role's mark, tag and note
   * colour. Reached only through `semantic.status.roles.beingEdited.model`. --tl-butter-strong
   */
  butterStrong: "#FED84C",
  coral: "#FFD6A6"
};
var support = {
  periwinkle: "#7CA7FF",
  mint: "#7DE3C7",
  lavender: "#C7B6FF",
  palePink: "#FFC4DE",
  /**
   * The pale ice tint the Overlays card (card 08) washes a `cyan`-tone dialog ribbon
   * with. Added by `setup-app-uplift-ds-overlays`; **name confirmed by Rob 2026-09-09**.
   *
   * It is deliberately NOT `brand.cyan` at a low alpha, which is what the paired-ink rule
   * would suggest: the card washes the ribbon with this tint and lights the glyph halo
   * with `brand.cyan`, so the two hues are doing different jobs in the same component.
   * The value existed in no design-project token — it was a card-local literal — which is
   * why the name is new rather than a reference. --tl-support-aqua
   */
  aqua: "#A0E6EB"
};
var CHART_PALETTE = [
  "#00BBCC",
  // 1  brand cyan   (Brand)
  "#B15FFF",
  // 2  violet       (Accent)
  "#FFC86B",
  // 3  butter       (Accent)
  "#FF6FBF",
  // 4  pink         (Accent)
  "#7CA7FF",
  // 5  periwinkle   (Supporting)
  "#7DE3C7",
  // 6  dusty mint   (Supporting)
  "#8077BE",
  // 7  blue-violet  (Ramp)
  "#FF8FAE",
  // 8  peach        (Accent)
  "#C7B6FF",
  // 9  lavender     (Supporting)
  "#5785D5"
  // 10 blue-muted   (Ramp)
];
var inkOn = {
  /** on `brand.cyan` / `CHART_PALETTE[0]`. --tl-ink-on-aqua */
  aqua: "#005F69",
  /** on `support.mint`. --tl-ink-on-mint */
  mint: "#0B6B70",
  /** on `support.periwinkle`. --tl-ink-on-peri */
  peri: "#2F5E9E",
  /** on `support.lavender`. --tl-ink-on-lav */
  lav: "#6B3FB5",
  /** on `accent.butter`. --tl-ink-on-butter */
  butter: "#8A5F00",
  /** on `accent.peach`. --tl-ink-on-pink */
  pink: "#8C2C4D",
  /** on `semanticColor.error`. --tl-ink-on-error */
  error: "#A32B2E",
  /** on `line.control` (= `ink[3]`) — the neutral "no signal" rail. --tl-ink-on-idle */
  idle: "#6D737E"
};
var inkGlass = {
  /** on `rgba(brand.cyan, .3)` over glass. Same value as `inkOn.aqua`. --tl-ink-glass-info */
  info: "#005F69",
  /** on `rgba(semanticColor.success, .42)` over glass. --tl-ink-glass-success */
  success: "#08545A",
  /** on `rgba(accent.butter, .3-.62)` over glass. --tl-ink-glass-warn */
  warn: "#7A4F00",
  /** on `rgba(semanticColor.error, .42)` over glass. --tl-ink-glass-error */
  error: "#8A1F22"
};
var statusSurface = {
  /** --tl-info-surface */
  info: "#E5F8FA",
  /** --tl-success-surface */
  success: "#E8FAF9",
  /** --tl-warn-surface */
  warn: "#FDF2D9",
  /** --tl-error-surface */
  error: "#FDEDED"
};
var inkTint = {
  /** on a `brand.cyan` tint. --tl-ink-tint-aqua */
  aqua: "#0E7B8C",
  /** on a `support.mint` tint. --tl-ink-tint-mint */
  mint: "#2A9C7B",
  /** on a `support.periwinkle` tint. --tl-ink-tint-peri */
  peri: "#4A78E0",
  /** on a `support.lavender` tint. --tl-ink-tint-lav */
  lav: "#7A66D6",
  /** on an `accent.butter` tint. --tl-ink-tint-butter */
  butter: "#B85C00",
  /** on an `accent.peach` tint. --tl-ink-tint-pink */
  pink: "#D26A9D"
};
var semanticColor = {
  success: "#5FD8DF",
  warn: "#D29200",
  /** in-product coral-red (banners, toasts). --tl-error */
  error: "#F15D5D",
  /** destructive / irreversible only. --tl-error-strong */
  errorStrong: "#C50F1F",
  /**
   * Solid badge fill — the success hue pushed dark enough to carry white text at badge
   * size. Pairs with `ink.inverse`. --tl-success-strong
   */
  successStrong: "#2FA9A0",
  /** Solid badge fill — the warn hue, same rule. Pairs with `ink.inverse`. --tl-warn-strong */
  warnStrong: "#C68500"
};
var color = {
  brand,
  ink,
  surface,
  line,
  disabled,
  accent,
  support,
  chartPalette: CHART_PALETTE,
  inkOn,
  semanticColor
};

// src/primitives/space.ts
var space = {
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
  10: "40px",
  12: "48px"
};
var inset = {
  1: "2px",
  2: "6px",
  3: "10px",
  4: "14px",
  5: "18px",
  6: "22px"
};
var icon = {
  xs: "12px",
  sm: "14px",
  md: "16px",
  lg: "18px",
  xl: "24px",
  "2xl": "32px"
};
var controlHeight = {
  /** The avatar's `xs` (card 05; `onboarding-persistent-scene-nav-bar`). --tl-control-h-xs */
  xs: "24px",
  /** --tl-control-h-sm */
  sm: "28px",
  /** --tl-control-h-md */
  md: "36px",
  /** The avatar's `lg` (card 05; `onboarding-persistent-scene-nav-bar`). --tl-control-h-lg */
  lg: "44px"
};

// src/primitives/radius.ts
var LG = "12px";
var XL = "16px";
var radius = {
  sm: "4px",
  /** default button / input. --tl-radius-md */
  md: "8px",
  /** card / toolbar. --tl-radius-lg */
  lg: LG,
  /** filter pill. --tl-radius-xl */
  xl: XL,
  pill: "999px",
  /**
   * The role alias for a control's corner — prefer it in components; it survives a scale retune.
   * The design source defines it as `var(--tl-radius-lg)`, so here it IS `lg` (the same constant,
   * so a retune of `lg` moves both); the CSS emission writes the `var()` (`css.ts`).
   * --tl-radius-control
   */
  control: LG,
  /**
   * A small control's corner. The design source also defines it as `var(--tl-radius-lg)`, so it
   * resolves to 12px here against the project's 10px until the radius scale is re-based (see
   * `design-source/PROVENANCE.md`, 2026-10-02). --tl-radius-control-sm
   */
  controlSm: LG,
  /**
   * A large glass container's corner: card 13's on-glass tab strip at the md density. The design
   * source defines it as a literal 12px, NOT an alias of `lg`, so it stays 12px when the radius
   * scale is re-based. --tl-radius-control-lg
   */
  controlLg: "12px",
  /**
   * A panel's corner — the nav bar (card 10's `.tl-nav`). The design source defines it as
   * `var(--tl-radius-xl)`, so here it IS `xl` and resolves to 16px against the project's 14px, held
   * with `xl` (`src/held-values.spec.ts`). --tl-radius-panel
   */
  panel: XL
};

// src/primitives/zIndex.ts
var zIndex = {
  /** A menu attached to a control, painted in the control's own flow. */
  dropdown: 10,
  /** Chrome that stays put while its pane scrolls (toolbars, sticky table headers). */
  sticky: 20,
  /** Viewport-fixed chrome. */
  fixed: 30,
  /** A scrim covering the surface beneath it. */
  overlay: 40,
  /** A dialog above its scrim. */
  modal: 50,
  /**
   * A transient layer that must clear in-card chrome AND the design system's own menu
   * panels (`Select`/`Combobox`/`MultiSelect` sit at `sticky`). `BaseDropdown`'s menu binds
   * here rather than to `dropdown` for that reason: at `dropdown` it would sit BELOW a
   * design-system menu panel, and at `sticky` it would TIE with one — and a tie is broken by
   * DOM order, which is the non-determinism this whole scale exists to remove.
   *
   * The design system's viewport toast stack (`ToastProvider`) binds here too, so a toast raised
   * while a dialog is open stays visible above `modal` (Rob, 2026-10-03).
   */
  popover: 60,
  /** Always last, above everything it describes. */
  tooltip: 70
};

// src/primitives/shadow.ts
var shadow = {
  /** Signature glass shadow stack — Panel, Card, Nav. --tl-shadow-glass */
  glass: "0px 16px 40px 0px rgba(0, 0, 0, 0.15), 0px 4px 16px 0px rgba(0, 0, 0, 0.08)",
  /** --tl-shadow-glass-sm */
  glassSm: "0px 0px 11px 1px rgba(228, 255, 254, 0.55), 0px 3px 11px 0px rgba(0, 0, 0, 0.05)",
  /** --tl-shadow-glass-hover */
  glassHover: "0px 0px 15px 2px rgba(228, 255, 254, 0.70), 0px 4px 15px 0px rgba(0, 0, 0, 0.08)",
  /** --tl-shadow-selected */
  selected: "0px 0px 12px 2px rgba(228, 255, 254, 1), 0px 2px 5px rgba(0, 0, 0, 0.90)",
  /**
   * Scrollbar thumb, drag state. --tl-shadow-scroll-thumb-drag
   *
   * Deliberately NOT `selected`, and the near-miss is the point: the frost glow layer is
   * identical, but the ink drop is `.55` where `selected` is `.90`. A 12px-wide pearl under a
   * `.90` black drop reads as grime rather than lift — `selected` is tuned for a card-sized
   * surface. Same family, own name, own value. Ruled 2026-07-30.
   */
  scrollThumbDrag: "0px 0px 12px 2px rgba(228, 255, 254, 1), 0px 2px 5px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.85)",
  /**
   * Ink drops on tinted surfaces, as CHANNEL TRIPLES. --tl-shadow-ink-glass-rgb
   *
   * Channels rather than finished colours so one token covers every opacity the design uses —
   * the same shape `scroll.solidBaseRgb` and `glassCard.tint` already take. Compose with
   * `rgba(var(--tl-shadow-ink-glass-rgb), a)` in CSS, or a template literal in TS.
   *
   * Glass ink is a violet-black, matching the lavender cast of the scene glaze; solid ink is a
   * blue-black for white admin surfaces. Deliberately never pure black — `Card`'s `on-scene`
   * drop still uses `rgba(0,0,0, glassCard.drop)`, which is the older idiom.
   *
   * Ruled 2026-07-31 (`docs/specs/design-ruling-banner-shadow-inks.md`), raised by `Banner`.
   * Used at: banner on-glass .10/.08, banner on-solid .04/.06, scrollbar on-solid thumb-drag .30.
   */
  inkGlassRgb: "40, 30, 80",
  /** Ink drop for white/Surface-1 panels, as channels. --tl-shadow-ink-solid-rgb */
  inkSolidRgb: "20, 24, 40",
  /** --tl-shadow-card-inset */
  cardInset: "inset 0 1px 3px rgba(255, 255, 255, 0.3), inset 0 -3px 8px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.05)",
  /** Gloss highlight overlay. --tl-gloss-overlay */
  glossOverlay: "linear-gradient(135deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.3) 40%, rgba(255, 255, 255, 0) 70%)"
};

// src/primitives/glass.ts
var glass = {
  bg: "rgba(255, 255, 255, 0.60)",
  bgSoft: "rgba(255, 255, 255, 0.40)",
  bgFaint: "rgba(255, 255, 255, 0.15)",
  /** hover. --tl-glass-bg-strong */
  bgStrong: "rgba(255, 255, 255, 0.80)",
  border: "rgba(255, 255, 255, 1)",
  blur: "blur(50px)",
  blurSm: "blur(6px)",
  blurXs: "blur(12px) saturate(90%)"
};
var glassCard = {
  blur: "10px",
  sat: "124%",
  /** rgb triplet (used inside rgba()). --tl-glass-card-tint */
  tint: "237,231,247",
  fill: "0.29",
  fillTop: "0.46",
  border: "0.59",
  bevel: "0.04",
  bevelSize: "13px",
  /** rgb triplet (frost glow). --tl-glass-card-frost */
  frost: "228,255,254",
  glow: "0.43",
  drop: "0.14"
};

// src/primitives/motion.ts
var motion = {
  easeUi: "ease",
  durFast: "0.2s",
  durMid: "0.3s",
  durSlow: "0.4s",
  /**
   * Focus "squish" overshoot. The sticker ring lands with the control popping forward like a
   * sticker being pressed on. Apply with the generated `.tl-focus-pop` utility, never a bespoke
   * animation — the utility carries the `prefers-reduced-motion` guard.
   * --tl-ease-pop / --tl-dur-pop / --tl-focus-pop-scale
   */
  easePop: "cubic-bezier(.34,1.56,.64,1)",
  durPop: "0.22s",
  focusPopScale: 1.03
};

// src/primitives/typography.ts
var fontSans = "'Lato', -apple-system, BlinkMacSystemFont, Arial, sans-serif";
var fontMono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
var weight = {
  regular: 400,
  medium: 500,
  semibold: 600
};
var tracking = {
  /** buttons — copied verbatim from PrimaryButton.tsx. --tl-tracking-button */
  button: "-0.154px",
  none: "0",
  /**
   * Uppercase mono labels — column headers, micro-caps. Uppercase mono needs the extra air to
   * stay legible at 10–11px; apply it wherever `text-transform: uppercase` is set.
   * --tl-tracking-label
   *
   * NOT the `.v-kpi` eyebrow: the Cards card specifies 0.14em there, mono at 10px against a
   * large value, where this 0.1em column-header tracking reads cramped. Two values on purpose
   * — see the comment at `KpiLabel` in `@repo/design-system`.
   */
  label: "0.1em"
};
var typeScale = {
  caption2: { fontSize: "10px", lineHeight: "14px" },
  captionCompact: { fontSize: "11px", lineHeight: "14px" },
  caption1: { fontSize: "12px", lineHeight: "16px" },
  body1: { fontSize: "14px", lineHeight: "20px" },
  button: { fontSize: "14px", lineHeight: "21px" },
  body2: { fontSize: "16px", lineHeight: "20px" },
  subtitle2: { fontSize: "16px", lineHeight: "22px" },
  subtitle1: { fontSize: "20px", lineHeight: "26px" },
  title3: { fontSize: "24px", lineHeight: "32px" },
  title2: { fontSize: "28px", lineHeight: "36px" },
  title1: { fontSize: "32px", lineHeight: "40px" }
};
var typeRole = {
  title1: { fontFamily: fontSans, ...typeScale.title1, fontWeight: weight.semibold },
  title2: { fontFamily: fontSans, ...typeScale.title2, fontWeight: weight.semibold },
  title3: { fontFamily: fontSans, ...typeScale.title3, fontWeight: weight.semibold },
  subtitle1: { fontFamily: fontSans, ...typeScale.subtitle1, fontWeight: weight.semibold },
  subtitle2: { fontFamily: fontSans, ...typeScale.subtitle2, fontWeight: weight.semibold },
  button: {
    fontFamily: fontSans,
    ...typeScale.button,
    fontWeight: weight.medium,
    letterSpacing: tracking.button
  },
  body2: { fontFamily: fontSans, ...typeScale.body2, fontWeight: weight.regular },
  body1: { fontFamily: fontSans, ...typeScale.body1, fontWeight: weight.regular },
  body1Strong: { fontFamily: fontSans, ...typeScale.body1, fontWeight: weight.medium },
  body1Stronger: { fontFamily: fontSans, ...typeScale.body1, fontWeight: weight.semibold },
  caption1: { fontFamily: fontSans, ...typeScale.caption1, fontWeight: weight.regular },
  caption1Strong: { fontFamily: fontSans, ...typeScale.caption1, fontWeight: weight.medium },
  captionCompact: { fontFamily: fontSans, ...typeScale.captionCompact, fontWeight: weight.regular },
  caption2: { fontFamily: fontSans, ...typeScale.caption2, fontWeight: weight.regular },
  caption2Strong: { fontFamily: fontSans, ...typeScale.caption2, fontWeight: weight.semibold }
};
var typography = {
  fontSans,
  fontMono,
  weight,
  tracking,
  scale: typeScale,
  role: typeRole
};

// src/primitives/ramp.ts
var SELECTED_RAMP = [
  "#E4FFFF",
  // 1  initial
  "#CFF7F7",
  // 2  crystal tint
  "#A0EBE6",
  // 3  teal light
  "#6FDBD2",
  // 4  teal med-light
  "#42DBD0",
  // 5  teal medium
  "#34CED7",
  // 6  teal accent
  "#1BB6D9",
  // 7  blue accent light (TL brand)
  "#0EAAE8",
  // 8  blue accent
  "#5BA8D7",
  // 9  blue bright
  "#5785D5",
  // 10 blue muted
  "#8077BE",
  // 11 blue violet
  "#A24489",
  // 12 pink medium
  "#D2377A"
  // 13 pink vibrant
];
var OFF_RAMP = [
  "#FAFAFA",
  "#E0E0E0",
  "#CFCFCF",
  "#BFBFBF",
  "#AFAFAF",
  "#9F9F9F",
  "#8F8F8F",
  "#808080",
  "#707070",
  "#606060",
  "#505050",
  "#404040",
  "#303030"
];
var INK_RAMP = [
  "#007A86",
  "#007A86",
  // 1-2
  "#0B6B70",
  "#0B6B70",
  "#0B6B70",
  // 3-5
  "#005F69",
  "#005F69",
  "#005F69",
  // 6-8
  "#2F5E9E",
  "#2F5E9E",
  // 9-10
  "#4A3D8E",
  // 11
  "#6B1F58",
  "#6B1F58"
  // 12-13
];
var OFF_INK_RAMP = [
  ink[4],
  ink[4],
  ink[3],
  ink[3],
  ink[3],
  ink[2],
  ink[2],
  ink[2],
  ink[1],
  ink[1],
  ink[1],
  ink[1],
  ink[1]
];
var HEATMAP_RAMP = [
  "#43DBD0",
  // 01
  "#6DD7B9",
  // 02
  "#93D3A5",
  // 03
  "#B9CF90",
  // 04
  "#DECB7D",
  // 05
  "#FEC46B",
  // 06
  "#F3A26F",
  // 07
  "#E98172",
  // 08
  "#DF6076",
  // 09
  "#D33A7A"
  // 10
];
var HEATMAP_RAMP_OPACITY = [
  0.1,
  0.3,
  0.42,
  0.52,
  0.6,
  0.67,
  0.73,
  0.78,
  0.82,
  0.86
];
var clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
var stopForValue = (value) => clamp(Math.ceil((Number.isFinite(value) ? value : 0) / 7.7), 1, 13);
var colorForValue = (value, selected = true) => {
  const idx = stopForValue(value) - 1;
  return (selected ? SELECTED_RAMP : OFF_RAMP)[idx];
};
var inkForValue = (value) => INK_RAMP[stopForValue(value) - 1];
var clampStop = (stop) => Number.isFinite(stop) ? clamp(Math.round(stop), 1, SELECTED_RAMP.length) : 7;
var rgbTriple = (hex) => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : h;
  const n = Number.parseInt(full, 16);
  return `${n >> 16 & 255},${n >> 8 & 255},${n & 255}`;
};
var rgba = (hex, alpha) => `rgba(${rgbTriple(hex)}, ${alpha})`;
var spreadIndex = (i, n) => {
  if (n <= 1) return 6;
  const step = (SELECTED_RAMP.length - 1) / (n - 1);
  return clamp(Math.round(i * step), 0, SELECTED_RAMP.length - 1);
};
var seriesColor = (i, n) => SELECTED_RAMP[spreadIndex(i, n)];
var offColor = (i, n) => OFF_RAMP[spreadIndex(i, n)];
var labelInk = (i, n) => INK_RAMP[spreadIndex(i, n)];
var greyInkFor = (stop) => stop >= 7 ? "#FFFFFF" : "#000000";
var offInk = (i, n) => greyInkFor(spreadIndex(i, n) + 1);
var ramp = {
  selected: SELECTED_RAMP,
  heatmap: HEATMAP_RAMP,
  heatmapOpacity: HEATMAP_RAMP_OPACITY,
  off: OFF_RAMP,
  ink: INK_RAMP,
  stopForValue,
  colorForValue,
  inkForValue,
  seriesColor,
  offColor,
  labelInk,
  greyInkFor,
  offInk
};

// src/status.ts
var roles = {
  /** All done: nothing more to do here. The success colour, everywhere in the app. */
  allDone: {
    mark: semanticColor.success,
    model: semanticColor.success,
    wash: rgba(semanticColor.success, 0.42),
    ink: inkGlass.success,
    tagTone: "success",
    noteTone: null
  },
  /**
   * Just changed: new or changed. A glow that fades for a brief arrival (`fadingGlow`); a lasting
   * mark, with a glyph for what changed, for a change to act on (`lastingMark`).
   */
  justChanged: {
    mark: support.mint,
    model: SELECTED_RAMP[3],
    wash: rgba(support.mint, 0.3),
    ink: inkOn.mint,
    tagTone: "mint",
    noteTone: "mint"
  },
  /** Being edited: what you are changing right now. Stands out on CCTV. */
  beingEdited: {
    mark: accent.butter,
    model: accent.butterStrong,
    wash: rgba(accent.butter, 0.32),
    ink: inkOn.butter,
    tagTone: "butter",
    noteTone: "butter"
  },
  /**
   * Work to do: a job left on this step, not a problem. On a step that cannot do the job it is a
   * problem instead (`statusRoleOnStep`).
   */
  workToDo: {
    mark: support.periwinkle,
    model: support.periwinkle,
    wash: rgba(support.periwinkle, 0.24),
    ink: inkOn.peri,
    tagTone: "peri",
    noteTone: "peri"
  },
  /** Problem with a fix: something is wrong, but there is a way out, even if it blocks Launch. */
  problem: {
    mark: accent.peach,
    model: accent.peach,
    wash: rgba(accent.peach, 0.28),
    ink: inkOn.pink,
    tagTone: "pink",
    noteTone: "pink"
  },
  /** Dead end: something has failed, or there is no way back. The only status red. */
  deadEnd: {
    mark: semanticColor.errorStrong,
    model: semanticColor.errorStrong,
    wash: semanticColor.errorStrong,
    ink: ink.inverse,
    tagTone: null,
    noteTone: null
  },
  /**
   * Information: a fact worth seeing that is not good or bad: where something came from, or more
   * detail about it. It never asks you to do anything.
   */
  information: {
    mark: support.lavender,
    model: support.lavender,
    wash: rgba(support.lavender, 0.34),
    ink: inkOn.lav,
    tagTone: "lavender",
    noteTone: "lavender"
  },
  /**
   * Neutral: no status. A decision already recorded, or a fact neither good nor bad. The `wash` is
   * a swatch or rail, never a background for text: `inkOn.idle` on `line.control` is about 3:1. A
   * neutral `Tag` draws no fill and keeps the surface's ink, so a label reads on the surface.
   */
  neutral: {
    mark: line.control,
    model: line.control,
    wash: line.control,
    ink: inkOn.idle,
    tagTone: "neutral",
    noteTone: "neutral"
  },
  /**
   * Stops go-live (Launch only): anything that stops go-live and can be fixed, in the softer error
   * red that banners and toasts use. Launch sets it item by item; no other step draws it.
   */
  stopsGoLive: {
    mark: semanticColor.error,
    model: semanticColor.error,
    wash: rgba(semanticColor.error, 0.42),
    ink: inkGlass.error,
    tagTone: "error",
    noteTone: "error"
  }
};
var status = {
  roles,
  /**
   * Selection: the frost glow, as the design system draws selection on glass
   * (`selectedOnGlassCss` in @repo/design-system). Not a status, so it never borrows a status hue.
   */
  selected: {
    fill: brand.frost,
    edge: brand.frost,
    shadow: `0 0 8px ${rgba(brand.frost, 0.7)}`,
    ink: inkOn.aqua
  },
  /** Hover: the bold dashed frost line, as the interactive `Card` draws hover. */
  hover: {
    edge: brand.frost,
    width: "2px",
    style: "dashed"
  },
  /** The fading "just arrived" glow, in the just-changed colour. */
  fadingGlow: `0 0 0 2px ${rgba(support.mint, 0.95)}, 0 0 16px 4px ${rgba(support.mint, 0.55)}`,
  /** The lasting just-changed mark: a ring that stays until acted on, with a glyph for what changed. */
  lastingMark: {
    edge: support.mint,
    width: "2px",
    style: "solid",
    ink: inkOn.mint
  },
  /** The engine's suggestion on the camera picture: the coral token, dashed, for now. */
  suggestion: {
    edge: accent.coral,
    style: "dashed"
  },
  /** A space's level is its fill, in neutral surface tints so status, selection and hover show on top. */
  spaceLevel: {
    zone: surface[4],
    section: surface[3],
    area: surface[2]
  }
};

// src/semantic.ts
var semantic = {
  text: {
    primary: ink[1],
    /**
     * The ink that goes ON a solid semantic fill (`feedback.error` / `errorStrong` /
     * `successStrong` / `warnStrong`, and the brand gel) — never on a pale `-surface` tint.
     * Not the same thing as `surface.base`: that is a surface, this is an ink.
     */
    onSolidFeedback: ink.inverse,
    secondary: ink[2],
    body: ink[3],
    muted: ink.muted,
    placeholder: ink[5],
    /**
     * TEXT-SAFE SUCCESS INK (spec §4.1-A2 / DS7) — the system-wide answer to "what
     * colour is a positive value when it genuinely is a verdict?".
     *
     * `feedback.success` (`#5FD8DF`) is a FILL/dot teal: ~1.7:1 on white, unreadable as
     * text and invisible as a 1px arrow glyph. The design source already carries its
     * sanctioned paired ink — `--tl-ink-on-aqua` (`#005F69`, ~7.4:1 on white) — under the
     * rule "an accent tint is a fill; the label on that surface takes the matching ink".
     * So this resolves by REUSE of a ratified value rather than by minting a new one.
     *
     * Consume THIS role, never `inkOn.aqua` directly and never a literal. It replaces
     * the hardcoded `#0B6B70` / `#C8484A` "invented green" pair that Rob's brief 18 kills
     * (`#0B6B70` is in fact `inkOn.mint` — sanctioned as an ink on mint, but it was doing
     * unsanctioned SEMANTIC duty, which is the actual defect).
     *
     * Positive deltas are **neutral ink** unless the direction is genuinely a judgement;
     * only then does this role apply. There is no green token and none is reintroduced.
     */
    success: inkOn.aqua,
    /**
     * TEXT-SAFE NEGATIVE INK — the symmetric partner to `success` above.
     *
     * `feedback.error` (`#F15D5D`) is the in-product coral used for banner/toast
     * FILLS; as text on white it is ~3.2:1, below AA body. `inkOn.error` (`#A32B2E`,
     * ~6.7:1) is its sanctioned paired ink, and is also much closer to the `#C8484A`
     * the design cards actually render for a negative delta than `--tl-error` is.
     *
     * NOTE FOR ROB (co-sign): brief 16 §2 names `--tl-error` for the down-delta while
     * flagging only `--tl-success` as the legibility problem. We took the ink for both
     * so delta text is symmetric and both directions clear AA — the same
     * fill-vs-ink rule the categorical-inks block states. Confirm or substitute; either
     * way it is a token-layer-only edit and no component changes.
     */
    error: inkOn.error
  },
  surface: {
    /** page/base surface. */
    base: surface[0],
    /** app background. */
    app: surface[1],
    panelTint: surface[2],
    accentWarm: surface[3],
    accentCool: surface[4],
    // --- Three panel treatments (I8): chart-library / composites pick per context ---
    /** opaque white KPI/panel card. */
    card: surface[0],
    /** THE glass card recipe (see glassCard primitive + .tl-glass-card). */
    glassCard,
    /** Remarkable overlay card — ported from TL-Remarkable brand.ts CARD_STYLE. */
    overlayCard: {
      background: "rgba(255, 255, 255, 0.65)",
      border: "1px solid #E5E7EB",
      borderRadius: "8px"
    },
    /**
     * **The scrim** — card 08's "Frosted Candy" backdrop, the one layer between the live
     * 3D twin and any modal. The card is explicit that there is exactly one: *"Every
     * dialog renders over this exact scrim."*
     *
     * The colour is `glassCard.frost` (#E4FFFE), **not** the card's literal
     * `rgba(228,255,255,0.35)`. Those differ by one unit in the blue channel; every other
     * frost in the design project is 228,255,254, and the card's value appears nowhere
     * else. **Rob chose the system frost, 2026-09-09** — holding two frost values one unit
     * apart is drift, and the difference is invisible. Composed from the token rather than
     * transcribed so a frost refresh reaches it.
     *
     * TS-only, like every role here — a `--tl-scrim-*` primitive would need a
     * design-project line that does not exist, and `parity.spec.ts` is bidirectional.
     */
    scrim: {
      background: `rgba(${glassCard.frost}, 0.35)`,
      backdropFilter: "blur(12px) saturate(0.8)"
    }
  },
  border: {
    /** default border. */
    default: line[2],
    /** faint row divider. */
    faint: line[4],
    /** crisper line on white. */
    strong: line[1],
    /** strong selected border. */
    selected: line.selected
  },
  interactive: {
    accent: brand.cyan,
    hover: brand.cyanHover,
    press: brand.cyanPress,
    focusRing: brand.focusRing,
    disabled
  },
  feedback: {
    success: semanticColor.success,
    warn: semanticColor.warn,
    error: semanticColor.error,
    errorStrong: semanticColor.errorStrong,
    /**
     * SOLID BADGE FILLS — the signal hue pushed dark enough to carry `text.onSolidFeedback`.
     * `feedback.success` / `feedback.warn` are pale FILL/dot values and cannot; these can.
     *
     * CONTRAST CAVEAT (from the design source): at badge size these land ~3.2:1 with the
     * inverse ink — enough for WCAG 1.4.11 on the fill as a non-text element, NOT 4.5:1 for
     * the label. Never pair such a badge with information available nowhere else.
     */
    successStrong: semanticColor.successStrong,
    warnStrong: semanticColor.warnStrong
  },
  /**
   * The onboarding status roles, selection, hover and change marks, and space level fills, from
   * Rob's status page. Built in `./status`; see there.
   */
  status
};

// src/statusRoleChecks.ts
var statusRoleOnStep = (role, owningStep, currentStep) => role === "workToDo" && owningStep !== currentStep ? "problem" : role;
var HEX = /#(?:[0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{4}|[0-9a-f]{3})(?![0-9a-f])/gi;
var RGB = /rgba?\(\s*(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})\s*(?:[,/]\s*[\d.]+%?\s*)?\)/gi;
var hexKey = (hex) => {
  const h = hex.slice(1);
  const full = h.length <= 4 ? `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : h.slice(0, 6);
  const n = Number.parseInt(full, 16);
  return `${n >> 16 & 255},${n >> 8 & 255},${n & 255}`;
};
var coloursIn = (s) => {
  const found = [];
  let m;
  HEX.lastIndex = 0;
  while ((m = HEX.exec(s)) !== null) {
    found.push({ at: m.index, colour: { text: m[0], key: hexKey(m[0]) } });
  }
  RGB.lastIndex = 0;
  while ((m = RGB.exec(s)) !== null) {
    found.push({
      at: m.index,
      colour: { text: m[0], key: `${Number(m[1])},${Number(m[2])},${Number(m[3])}` }
    });
  }
  return found.sort((a, b) => a.at - b.at).map((f) => f.colour);
};
var walkStrings = (value, path, seen, visit) => {
  if (typeof value === "string") {
    visit(value, path);
    return;
  }
  if (value === null || typeof value !== "object" || seen.has(value)) return;
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((item, i) => walkStrings(item, `${path}[${i}]`, seen, visit));
    return;
  }
  for (const [k, v] of Object.entries(value)) {
    walkStrings(v, path ? `${path}.${k}` : k, seen, visit);
  }
};
var keysOf = (value) => {
  const keys = /* @__PURE__ */ new Set();
  walkStrings(value, "", /* @__PURE__ */ new Set(), (s) => coloursIn(s).forEach((c) => keys.add(c.key)));
  return keys;
};
var STATUS_COLOURS = keysOf(status);
var statusTableViolations = (table, options = {}) => {
  const allowed = keysOf(options.allow ?? []);
  const violations = [];
  walkStrings(table, "", /* @__PURE__ */ new Set(), (s, path) => {
    for (const c of coloursIn(s)) {
      if (!STATUS_COLOURS.has(c.key) && !allowed.has(c.key))
        violations.push({ path, value: c.text });
    }
  });
  return violations;
};

// src/chart.ts
var BAND_STOP_LOW = 2;
var BAND_STOP_TOP = 12;
var BAND_STOPS_FOUR = [2, 6, 10, 12];
var utilisationBandStop = (i, count) => {
  if (!Number.isFinite(i) || !Number.isFinite(count) || count < 2) return BAND_STOP_LOW;
  const clamped = Math.min(Math.max(Math.round(i), 0), Math.round(count) - 1);
  if (Math.round(count) === 4) return BAND_STOPS_FOUR[clamped];
  const step = (BAND_STOP_TOP - BAND_STOP_LOW) / (Math.round(count) - 1);
  return Math.round(BAND_STOP_LOW + clamped * step);
};
var utilisationBandColour = (i, count) => SELECTED_RAMP[utilisationBandStop(i, count)];
var utilisationBandInk = (i, count) => INK_RAMP[utilisationBandStop(i, count)];
var axisLabel = {
  fontFamily: fontMono,
  fontSize: "10px",
  letterSpacing: ".14em",
  textTransform: "uppercase"
};
var base = {
  line: {
    tension: 0.38,
    borderWidth: 2.6,
    capStyle: "round",
    joinStyle: "round",
    // Card 17's `areaFill` default top-alpha; the brief says only "soft".
    fillTopAlpha: 0.26,
    fillTopAlphaMulti: 0.16
  },
  point: {
    radius: 2.4,
    hoverRadius: 6,
    hitRadius: 14,
    hoverRingWidth: 2.5,
    hoverRingColor: surface[0]
  },
  bar: {
    borderRadius: 7,
    borderSkipped: false,
    maxThickness: 40,
    fadeTailVertical: 0,
    fadeTailHorizontal: 0.35
  },
  grid: {
    // `--tl-line-4`, the faint row divider — brief 17 §0's `#EEF1F6` exactly.
    color: line[4],
    xDisplay: false,
    borderDisplay: false
  },
  tick: {
    fontFamily: fontMono,
    fontSize: "10px",
    color: ink[4],
    categoryFontFamily: fontSans,
    categoryFontSize: typeRole.caption1.fontSize,
    categoryColor: ink[1]
  },
  peakBand: {
    // `--tl-frost` at 0.55. Brief 17 §2 names `rgba(228,255,254,.55)` and `brand.frostGlow`
    // IS that value, so the brief's band is a REUSE of a sanctioned token, not a new one.
    // (The superseded PROVISIONAL `annotationBand` carried the same frost at 0.35; the
    // brief's 0.55 wins under Peter's DS-authority ruling — deviation D-8.)
    fill: brand.frostGlow,
    // Card 17 strokes the band in the brand-accent ramp stop at low alpha, not a neutral
    // divider grey — the band edge belongs to the band, not to the chart chrome.
    edgeColor: rgba(SELECTED_RAMP[6], 0.28),
    edgeDash: [4, 4]
  },
  animation: {
    firstPaint: false,
    // `--tl-dur-fast` is 0.2s — exactly the brief's "tween ≤200ms". Reused, not restated.
    tweenDuration: motion.durFast
  }
};
var chartTheme = {
  base,
  categorical: CHART_PALETTE,
  chartColour: (i) => CHART_PALETTE[
    // Wrap rather than run off the end; guard a negative/NaN index to the first hue.
    Number.isFinite(i) && i > 0 ? Math.floor(i) % CHART_PALETTE.length : 0
  ],
  axis: {
    label: axisLabel,
    tick: ink[4],
    title: ink[3]
  },
  grid: {
    line: line[2],
    faint: line[4],
    border: line[1]
  },
  legend: {
    fontFamily: fontSans,
    fontSize: typeRole.caption1.fontSize,
    fontWeight: weight.regular,
    text: ink[2]
  },
  tooltip: {
    fontFamily: fontSans,
    bodyFontFamily: fontMono,
    fontSize: typeRole.caption1.fontSize,
    // Re-pointed at CP4 from `rgba(255,255,255,0.65)`: see the interface note — canvas has
    // no backdrop-filter, so card 17 renders the glass as near-opaque white.
    surface: rgba(surface[0], 0.97),
    text: ink[1],
    // Re-pointed at CP4 from `radius.md` (8px). Brief 17 §0 and card 17 both specify
    // `cornerRadius 10`, and there is no 10px step in the radius scale; a chart-role px
    // value belongs here, where `axis.label.fontSize` already sets the precedent.
    radius: "10px",
    shadow: shadow.glassSm,
    usePointStyle: true
  },
  series: { seriesColor, offColor, labelInk },
  // RATIFIED (brief 17 §4) — ramp stops 3/7/11, confirmed byte-identical to Rob's target
  // (#A0EBE6 → #1BB6D9 → #8077BE). The bands are a MAGNITUDE scale.
  //
  // `overCapacity` RE-POINTED AT CP4 (deviation D-3, now landed). It was
  // `semanticColor.errorStrong` (`#C50F1F`) through PR-1, deliberately, because it is
  // consumed by the shipped 1.1 usage-histogram and re-pointing it is a VISIBLE change —
  // which DS11 excludes from the foundations PR. It now steps the ramp's top stop with
  // the other three, so the four bands walk the primary ramp end to end with no semantic
  // red at all. Over 100% is an EXPECTED MAGNITUDE, not a verdict (brief 17 §4/§6), and
  // `--tl-error-strong` is reserved for destructive UI and is never a data colour.
  utilisationBands: {
    low: SELECTED_RAMP[2],
    // stop 3  (#A0EBE6 teal-light)
    medium: SELECTED_RAMP[6],
    // stop 7  (#1BB6D9 blue-accent-light)
    high: SELECTED_RAMP[10],
    // stop 11 (#8077BE blue-violet)
    overCapacity: SELECTED_RAMP[12]
    // stop 13 (#D2377A pink-vibrant)
  },
  utilisationBandColour,
  utilisationBandInk,
  // 🚨 RE-RULED (Rob §8.2 item 9, 2026-08-27) — the CATEGORICAL ramp in sequence. Superseded:
  // brief 17 §3's cyan + off-ramp grey, kept in the interface note above rather than deleted.
  //
  // ⭐ A SIDE EFFECT WORTH KNOWING: this takes the pair OFF `SELECTED_RAMP` entirely, so the
  // A/B colours no longer move when the ramp's value→stop mapping changes.
  comparison: {
    a: CHART_PALETTE[0],
    // categorical 1 (#00BBCC brand cyan)
    b: CHART_PALETTE[1],
    // categorical 2 (#B15FFF violet)
    // Both series carry the area gradient (brief 17 §2 — "fills are on for all line charts,
    // comparison included"), with B fainter so A reads as the subject and B as the
    // reference. Values from card 17's comparison board.
    fillTopAlphaA: 0.18,
    fillTopAlphaB: 0.1,
    dash: [5, 4]
  },
  // Card 16 §6's `.tl-seg`, verbatim:
  //   .tl-seg        { background:--tl-surface-2; border:1px --tl-line-2; radius:--tl-radius-md;
  //                    padding:2px; gap:2px }
  //   .tl-seg button { font:500 12px sans; color:--tl-ink-3; padding:5px 10px; radius:6px }
  //   .tl-seg .on    { background:--tl-surface-0; color:--tl-ink;
  //                    box-shadow:0 1px 2px rgba(40,30,80,.12) }
  //
  // CORRECTED 2026-07-31 (Peter's ruling: defer to the design system). The first pass read
  // brief 19 §1's "`--tl-surface-0` on-pill" as the pill's INK and left the shipped
  // brand-cyan fill with white text in place. It is the pill's BACKGROUND: the selected pill
  // is a WHITE RAISED pill with dark ink on a recessed track, and it reads as "on" by
  // elevation rather than by colour. That also settles brief 19's "flat brand cyan is not a
  // control fill" — the pill was never meant to be cyan, exactly like the checkbox.
  segmented: {
    trackSurface: surface[2],
    trackBorder: line[2],
    trackRadius: radius.md,
    trackPad: "2px",
    trackGap: "2px",
    fontSize: typeRole.caption1.fontSize,
    // caption1 IS 12px
    fontWeight: weight.medium,
    // the card's `font:500`
    padY: "5px",
    padX: "10px",
    // 6px sits between `radius.sm` (4) and `radius.lg` (12) with no scale step of its own —
    // this control's geometry, like `padY`/`padX` (and `axis.label.fontSize` before them).
    pillRadius: "6px",
    ink: ink[3],
    onPillSurface: surface[0],
    onPillInk: ink[1],
    // A lift, not a glow: the `shadow` scale's members are all frost/glass blooms, and none is
    // this. Card-authored, carried verbatim per the Button/Tag precedent in this package.
    onPillShadow: "0 1px 2px rgba(40, 30, 80, 0.12)"
  },
  // PROVISIONAL — ratify in design project (epic §6B).
  states: {
    empty: { text: ink.muted, surface: surface[1] },
    stale: { overlay: "rgba(255, 255, 255, 0.60)", text: ink[4] },
    skeleton: { base: surface[1], drift: surface[3], gloss: "rgba(228, 255, 254, 0.45)" }
  }
};

// src/css.ts
var RAMP_SLUGS = [
  "initial",
  "crystal-tint",
  "teal-light",
  "teal-med-light",
  "teal-medium",
  "teal-accent",
  "blue-accent-light",
  "blue-accent",
  "blue-bright",
  "blue-muted",
  "blue-violet",
  "pink-medium",
  "pink-vibrant"
];
function buildCssVariables() {
  const v = [];
  const add = (name, value) => {
    v.push({ name, value: String(value) });
  };
  add("--tl-cyan", brand.cyan);
  add("--tl-cyan-hover", brand.cyanHover);
  add("--tl-cyan-press", brand.cyanPress);
  add("--tl-cyan-selected", brand.cyanSelected);
  add("--tl-brand-gel-idle", brand.gelIdle);
  add("--tl-brand-gel-hover", brand.gelHover);
  add("--tl-brand-gel-press", brand.gelPress);
  add("--tl-brand-glow", brand.glow);
  add("--tl-brand-glow-hover", brand.glowHover);
  add("--tl-brand-press-inset", brand.pressInset);
  add("--tl-aqua", brand.aqua);
  add("--tl-brand-gradient", brand.gradient);
  add("--tl-frost", brand.frost);
  add("--tl-frost-glow", brand.frostGlow);
  add("--tl-cyan-deep", brand.cyanDeep);
  add("--tl-focus-line", focusCss.line);
  add("--tl-focus-bloom", focusCss.bloom);
  add("--tl-focus", focusCss.ring);
  add("--tl-focus-tight", focusCss.tight);
  add("--tl-focus-on-dark", focusCss.onDark);
  add("--tl-brand-focus-ring", focusCss.brandFocusRing);
  RAMP_SLUGS.forEach((slug, i) => add(`--tl-ramp-${slug}`, SELECTED_RAMP[i]));
  RAMP_SLUGS.forEach((slug, i) => add(`--tl-ramp-${slug}-off`, OFF_RAMP[i]));
  add("--tl-ramp-primary-03", SELECTED_RAMP[2]);
  add("--tl-ramp-primary-04", SELECTED_RAMP[3]);
  add("--tl-ramp-primary-06", SELECTED_RAMP[5]);
  add("--tl-ramp-primary-07", SELECTED_RAMP[6]);
  HEATMAP_RAMP.forEach((hex, i) => add(`--tl-ramp-heatmap-${String(i + 1).padStart(2, "0")}`, hex));
  add("--tl-ink", ink[1]);
  add("--tl-ink-2", ink[2]);
  add("--tl-ink-3", ink[3]);
  add("--tl-ink-4", ink[4]);
  add("--tl-ink-5", ink[5]);
  add("--tl-ink-muted", ink.muted);
  add("--tl-surface-0", surface[0]);
  add("--tl-surface-1", surface[1]);
  add("--tl-surface-2", surface[2]);
  add("--tl-surface-3", surface[3]);
  add("--tl-surface-4", surface[4]);
  add("--tl-line-1", line[1]);
  add("--tl-line-2", line[2]);
  add("--tl-line-3", line[3]);
  add("--tl-line-4", line[4]);
  add("--tl-line-selected", line.selected);
  add("--tl-surface-hover", surface.hover);
  add("--tl-disabled", disabled);
  add("--tl-line-control", line.control);
  add("--tl-scroll-track-glass", scroll.trackGlass);
  add("--tl-scroll-track-glass-recess", scroll.trackGlassRecess);
  add("--tl-scroll-solid-base", scroll.solidBase);
  add("--tl-scroll-solid-base-rgb", scroll.solidBaseRgb);
  add("--tl-scroll-solid-recess", scroll.solidRecess);
  add("--tl-accent-pink", accent.pink);
  add("--tl-accent-violet", accent.violet);
  add("--tl-accent-peach", accent.peach);
  add("--tl-accent-butter", accent.butter);
  add("--tl-butter-strong", accent.butterStrong);
  add("--tl-accent-coral", accent.coral);
  add("--tl-support-periwinkle", support.periwinkle);
  add("--tl-support-mint", support.mint);
  add("--tl-support-lavender", support.lavender);
  add("--tl-support-pale-pink", support.palePink);
  add("--tl-support-aqua", support.aqua);
  CHART_PALETTE.forEach((hex, i) => add(`--tl-chart-${i + 1}`, hex));
  add("--tl-ink-on-aqua", inkOn.aqua);
  add("--tl-ink-on-mint", inkOn.mint);
  add("--tl-ink-on-peri", inkOn.peri);
  add("--tl-ink-on-lav", inkOn.lav);
  add("--tl-ink-on-butter", inkOn.butter);
  add("--tl-ink-on-pink", inkOn.pink);
  add("--tl-ink-on-error", inkOn.error);
  add("--tl-ink-on-idle", inkOn.idle);
  add("--tl-ink-tint-aqua", inkTint.aqua);
  add("--tl-ink-tint-mint", inkTint.mint);
  add("--tl-ink-tint-peri", inkTint.peri);
  add("--tl-ink-tint-lav", inkTint.lav);
  add("--tl-ink-tint-butter", inkTint.butter);
  add("--tl-ink-tint-pink", inkTint.pink);
  add("--tl-info-surface", statusSurface.info);
  add("--tl-success-surface", statusSurface.success);
  add("--tl-warn-surface", statusSurface.warn);
  add("--tl-error-surface", statusSurface.error);
  add("--tl-ink-glass-info", inkGlass.info);
  add("--tl-ink-glass-success", inkGlass.success);
  add("--tl-ink-glass-warn", inkGlass.warn);
  add("--tl-ink-glass-error", inkGlass.error);
  add("--tl-success", semanticColor.success);
  add("--tl-warn", semanticColor.warn);
  add("--tl-error", semanticColor.error);
  add("--tl-error-strong", semanticColor.errorStrong);
  add("--tl-success-strong", semanticColor.successStrong);
  add("--tl-warn-strong", semanticColor.warnStrong);
  add("--tl-ink-inverse", ink.inverse);
  add("--tl-glass-bg", glass.bg);
  add("--tl-glass-bg-soft", glass.bgSoft);
  add("--tl-glass-bg-faint", glass.bgFaint);
  add("--tl-glass-bg-strong", glass.bgStrong);
  add("--tl-glass-border", glass.border);
  add("--tl-glass-blur", glass.blur);
  add("--tl-glass-blur-sm", glass.blurSm);
  add("--tl-glass-blur-xs", glass.blurXs);
  add("--tl-shadow-glass", shadow.glass);
  add("--tl-shadow-glass-sm", shadow.glassSm);
  add("--tl-shadow-glass-hover", shadow.glassHover);
  add("--tl-shadow-selected", shadow.selected);
  add("--tl-shadow-scroll-thumb-drag", shadow.scrollThumbDrag);
  add("--tl-shadow-ink-glass-rgb", shadow.inkGlassRgb);
  add("--tl-shadow-ink-solid-rgb", shadow.inkSolidRgb);
  add("--tl-shadow-card-inset", shadow.cardInset);
  add("--tl-gloss-overlay", shadow.glossOverlay);
  add("--tl-radius-sm", radius.sm);
  add("--tl-radius-md", radius.md);
  add("--tl-radius-lg", radius.lg);
  add("--tl-radius-xl", radius.xl);
  add("--tl-radius-pill", radius.pill);
  add("--tl-radius-control", "var(--tl-radius-lg)");
  add("--tl-radius-control-sm", "var(--tl-radius-lg)");
  add("--tl-radius-control-lg", radius.controlLg);
  add("--tl-radius-panel", "var(--tl-radius-xl)");
  add("--tl-control-h-xs", controlHeight.xs);
  add("--tl-control-h-sm", controlHeight.sm);
  add("--tl-control-h-md", controlHeight.md);
  add("--tl-control-h-lg", controlHeight.lg);
  Object.entries(space).forEach(([k, val]) => add(`--tl-space-${k}`, val));
  Object.entries(inset).forEach(([k, val]) => add(`--tl-inset-${k}`, val));
  Object.entries(icon).forEach(([k, val]) => add(`--tl-icon-${k}`, val));
  add("--tl-ease-ui", motion.easeUi);
  add("--tl-dur-fast", motion.durFast);
  add("--tl-dur-mid", motion.durMid);
  add("--tl-ease-pop", motion.easePop);
  add("--tl-dur-pop", motion.durPop);
  add("--tl-focus-pop-scale", motion.focusPopScale);
  add("--tl-dur-slow", motion.durSlow);
  add("--tl-font-sans", fontSans);
  add("--tl-font-mono", fontMono);
  Object.keys(typeScale).forEach((step) => {
    add(`--tl-fs-${step}`, typeScale[step].fontSize);
    add(`--tl-lh-${step}`, typeScale[step].lineHeight);
  });
  add("--tl-fw-regular", weight.regular);
  add("--tl-fw-medium", weight.medium);
  add("--tl-fw-semibold", weight.semibold);
  add("--tl-tracking-button", tracking.button);
  add("--tl-tracking-none", tracking.none);
  add("--tl-tracking-label", tracking.label);
  add("--tl-glass-card-blur", glassCard.blur);
  add("--tl-glass-card-sat", glassCard.sat);
  add("--tl-glass-card-tint", glassCard.tint);
  add("--tl-glass-card-fill", glassCard.fill);
  add("--tl-glass-card-fill-top", glassCard.fillTop);
  add("--tl-glass-card-border", glassCard.border);
  add("--tl-glass-card-bevel", glassCard.bevel);
  add("--tl-glass-card-bevel-size", glassCard.bevelSize);
  add("--tl-glass-card-frost", glassCard.frost);
  add("--tl-glass-card-glow", glassCard.glow);
  add("--tl-glass-card-drop", glassCard.drop);
  return v;
}
export {
  CHART_PALETTE,
  CYAN_DEEP,
  HEATMAP_RAMP,
  HEATMAP_RAMP_OPACITY,
  INK_RAMP,
  OFF_INK_RAMP,
  OFF_RAMP,
  SELECTED_RAMP,
  accent,
  brand,
  buildCssVariables,
  chartTheme,
  clampStop,
  color,
  colorForValue,
  controlHeight,
  disabled,
  focus,
  focusCss,
  fontMono,
  fontSans,
  glass,
  glassCard,
  greyInkFor,
  icon,
  ink,
  inkForValue,
  inkGlass,
  inkOn,
  inkTint,
  inset,
  labelInk,
  line,
  motion,
  offColor,
  offInk,
  radius,
  ramp,
  rgbTriple,
  rgba,
  scroll,
  semantic,
  semanticColor,
  seriesColor,
  shadow,
  space,
  statusRoleOnStep,
  statusSurface,
  statusTableViolations,
  stopForValue,
  support,
  surface,
  tracking,
  typeRole,
  typeScale,
  typography,
  weight,
  zIndex
};
